import {
  format,
  endOfDay,
  startOfDay,
  isBefore,
  isAfter,
  compareAsc,
  differenceInMinutes,
  areIntervalsOverlapping,
} from "date-fns";
import React from "react";

import {
  getHoursInDay,
  TIME_FORMAT,
  MINUTES_IN_ONE_DAY,
} from "../lib/datetime";
import { stylex } from "./stylex";
import { Text } from "./text";
import { AutoSizer } from "./auto_sizer";
import { useThrottledCallback } from "../hooks/use_throttled_callback";
import { Divider } from "./divider";
import { cssVariables } from "./css_variables";
import { useDebouncedCallback } from "../hooks/use_debounced_callback";

const CELL_WIDTH = 280.0;
const CELL_HEIGHT = 48.0;
const HEADER_HEIGHT = 64.0;
const LEFT_COLUMN_WIDTH = 64.0;

interface DayEvent {
  startTime: Date;
  endTime: Date;
}

interface Day<TEvent extends DayEvent> {
  date: Date;
  events: TEvent[];
}

interface DaysCalendarProps<TEvent extends DayEvent, TDay extends Day<TEvent>> {
  days?: TDay[];
  currentDate?: Date;
  onNew?: (event: Event, key: string) => void;
  onPress?: (event: TEvent) => void;
  renderHeaderCell?: (props: { day: TDay }) => React.ReactNode;
  renderEvent?: (props: { event: TEvent }) => React.ReactNode;
  dayKeyExtractor?: (day: TDay) => string;
  eventKeyExtractor?: (event: TEvent) => string;
}

export const DaysCalendar = <TEvent extends DayEvent, TDay extends Day<TEvent>>(
  props: DaysCalendarProps<TEvent, TDay>
) => {
  const {
    days = [],
    renderHeaderCell = () => null,
    renderEvent = () => null,
    dayKeyExtractor,
    eventKeyExtractor,
  } = props;

  const headerNodeRef = React.useRef<HTMLDivElement | null>(null);
  const hoursColumnNodeRef = React.useRef<HTMLDivElement | null>(null);
  const gridNodeRef = React.useRef<HTMLDivElement | null>(null);
  const scrollLockRef = React.useRef<HTMLDivElement | null>(null);

  const releaseLock = useDebouncedCallback(() => {
    scrollLockRef.current = null;
  }, 30);

  const handleOnHeaderScroll = useThrottledCallback(
    (_event: React.UIEvent<HTMLDivElement>) => {
      if (
        scrollLockRef.current !== null &&
        scrollLockRef.current !== headerNodeRef.current
      ) {
        return;
      }

      const header = headerNodeRef.current;
      const grid = gridNodeRef.current;

      scrollLockRef.current = headerNodeRef.current;

      if (header && grid) {
        grid.scrollTo(header.scrollLeft, grid.scrollTop);
      }

      releaseLock();
    },
    16,
    [releaseLock]
  );

  const handleOnHoursColumnScroll = useThrottledCallback(
    (_event: React.UIEvent<HTMLDivElement>) => {
      if (
        scrollLockRef.current !== null &&
        scrollLockRef.current !== hoursColumnNodeRef.current
      ) {
        return;
      }

      const hoursColumn = hoursColumnNodeRef.current;
      const grid = gridNodeRef.current;

      scrollLockRef.current = hoursColumnNodeRef.current;

      if (hoursColumn && grid) {
        grid.scrollTo(grid.scrollLeft, hoursColumn.scrollTop);
      }

      releaseLock();
    },
    16,
    [releaseLock]
  );

  const handleOnGridScroll = useThrottledCallback(
    (_event: React.UIEvent<HTMLDivElement>) => {
      if (
        scrollLockRef.current !== null &&
        scrollLockRef.current !== gridNodeRef.current
      ) {
        return;
      }

      const header = headerNodeRef.current;
      const hoursColumn = hoursColumnNodeRef.current;
      const grid = gridNodeRef.current;

      scrollLockRef.current = gridNodeRef.current;

      if (header && hoursColumn && grid) {
        header.scrollTo(grid.scrollLeft, header.scrollTop);
        hoursColumn.scrollTo(hoursColumn.scrollLeft, grid.scrollTop);
      }

      releaseLock();
    },
    16,
    [releaseLock]
  );

  return (
    <AutoSizer>
      {({ width, height }) => {
        const rightSideWidth = width - LEFT_COLUMN_WIDTH;
        const offsetHeaderHeight = height - HEADER_HEIGHT;
        const hours = getHoursInDay();
        const gridHeight = hours.length * CELL_HEIGHT;
        const gridWidth = days.length * CELL_WIDTH;

        return (
          <div style={{ ...styles("root"), width, height }}>
            <div style={styles("leftSide")}>
              <div style={styles("topLeftCell")}></div>
              <div
                ref={hoursColumnNodeRef}
                onScroll={handleOnHoursColumnScroll}
                className="hide-scrollbar"
                style={{
                  ...styles("hoursColumn"),
                  height: offsetHeaderHeight,
                }}
              >
                {hours.map((hour, index) => {
                  // The text has to be aligned with the hour lines
                  // and also has to have equal scrollHeight to grid
                  if (index === 0) {
                    return (
                      <div
                        key={hour.getHours()}
                        style={styles("hoursEmptyCell")}
                      />
                    );
                  }

                  const isLast = index === hours.length - 1;

                  return (
                    <div
                      key={hour.getHours()}
                      style={styles("hoursCell", isLast && "hoursLastCell")}
                    >
                      <Text>{format(hour, TIME_FORMAT)}</Text>
                    </div>
                  );
                })}
              </div>
            </div>
            <div
              style={{
                ...styles("rightSide"),
              }}
            >
              <div
                ref={headerNodeRef}
                onScroll={handleOnHeaderScroll}
                className="hide-scrollbar"
                style={{
                  ...styles("header"),
                  width: rightSideWidth,
                }}
              >
                {days.map((day, index) => (
                  <div
                    key={dayKeyExtractor ? dayKeyExtractor(day) : index}
                    style={styles("headerCell")}
                  >
                    {renderHeaderCell({ day })}
                  </div>
                ))}
              </div>
              <div
                ref={gridNodeRef}
                onScroll={handleOnGridScroll}
                style={{
                  ...styles("grid"),
                  width: rightSideWidth,
                  height: offsetHeaderHeight,
                }}
              >
                <div
                  style={{
                    ...styles("hourLinesWrapper"),
                    height: gridHeight,
                    width: gridWidth,
                  }}
                >
                  {hours.map((hour) => (
                    <div key={hour.getHours()} style={styles("hourLine")}>
                      <Divider />
                    </div>
                  ))}
                </div>
                {days.map((day, index) => {
                  const eventCoordinates = toCoordinates({
                    events: day.events,
                    day: day.date,
                    maxWidth: CELL_WIDTH,
                    maxHeight: gridHeight,
                  });

                  return (
                    <div
                      key={dayKeyExtractor ? dayKeyExtractor(day) : index}
                      style={{ ...styles("gridColumn"), height: gridHeight }}
                    >
                      <div style={styles("gridColumnBackground")} />
                      {eventCoordinates.map((eventCoordinate, index) => (
                        <button
                          key={
                            eventKeyExtractor
                              ? eventKeyExtractor(eventCoordinate.event)
                              : `${index}-${eventCoordinate.top}-${eventCoordinate.left}`
                          }
                          style={{
                            ...styles("event"),
                            left: eventCoordinate.left,
                            width: eventCoordinate.width,
                            height: eventCoordinate.height,
                            top: eventCoordinate.top,
                          }}
                        >
                          {renderEvent({ event: eventCoordinate.event })}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      }}
    </AutoSizer>
  );
};

interface EventCoordinate<TEvent extends DayEvent> {
  height: number;
  top: number;
  width: number;
  left: number;
  event: TEvent;
}

function toCoordinates<TEvent extends DayEvent>(params: {
  events: TEvent[];
  day: Date;
  maxWidth: number;
  maxHeight: number;
}): EventCoordinate<TEvent>[] {
  const { events, maxWidth, maxHeight, day } = params;
  const coordinates: EventCoordinate<TEvent>[] = [];

  // Order events by their start time
  events.sort((a, b) => compareAsc(a.startTime, b.startTime));

  // #region Create coordinates and give them top position and height
  const eod = endOfDay(day);
  const sod = startOfDay(day);
  const pxPerMinute = maxHeight / MINUTES_IN_ONE_DAY;

  events.forEach((event) => {
    let coordinate: EventCoordinate<TEvent> = {
      event,
      height: 0,
      top: 0,
      left: 0,
      width: 0,
    };

    // When start time is before the start of day, it should be at top most
    if (isBefore(event.startTime, sod)) {
      coordinate.top = 0;
      // Otherwise it is the difference between start of the day until the event start time
    } else {
      coordinate.top = differenceInMinutes(event.startTime, sod) * pxPerMinute;
    }

    // When end time is after end of the day, the height should be cut off by the offset
    if (isAfter(event.endTime, eod)) {
      coordinate.height =
        differenceInMinutes(eod, event.startTime) * pxPerMinute;
      // If the event start time was before the start of day, we should offset that difference
    } else if (isBefore(event.startTime, sod)) {
      coordinate.height =
        differenceInMinutes(event.endTime, event.startTime) * pxPerMinute -
        differenceInMinutes(sod, event.startTime) * pxPerMinute;
      // Otherwise it is the length of the event
    } else {
      coordinate.height =
        differenceInMinutes(event.endTime, event.startTime) * pxPerMinute;
    }

    coordinates.push(coordinate);
  });
  // #endregion

  // #region Update coordinates with width and offsetWidth
  // Note that we want coordinates to not overlap each other when shown in column
  //
  // How it works:
  // Assuming we have 5 events, denoted by [startTime, endTime]
  // [[0, 3], [2.5, 4], [3, 5], [6, 8], [7, 9]] (there will be 2 overlapping groups)
  //
  // In the first iteration, where start and end is 0
  // prevCoordinate = [0, 3], nextCoordinate = [2.5, 4]
  // the ranges are overlapping so end will increment, start=0 and end=1
  // Next iteration:
  // prevCoordinate = [2.5, 4], nextCoordinate = [3, 5]
  // the ranges are overlapping so end will increment, start=0 and end=2
  // Next iteration:
  // prevCoordinate = [3, 5], nextCoordinate = [6, 8]
  // the ranges are NOT overlapping. we take the sublist of events from start=0 to end=2
  // and add it to overlapping coordinates group list [[[0, 3], [2.5, 4], [3, 5]]]
  // then we set start and end to be end + 1, so start=3 and end=3
  // Next iteration:
  // prevCoordinate = [6, 8], nextCoordinate = [7, 9]
  // the ranges are overlapping so end will increment, start=3 and end=4
  // Next iteration:
  // end is 4, which is the last item, we add the remainder of the events to the group list
  // from start=3 and end=4
  // we increment end to exit the while loop
  const overlappingCoordinatesGroup: EventCoordinate<TEvent>[][] = [];

  let start = 0;
  let end = 0;

  while (end !== events.length) {
    if (end === events.length - 1) {
      overlappingCoordinatesGroup.push(coordinates.slice(start));
      end++;
      continue;
    }

    const prevCoordinate = coordinates[end];
    const nextCoordinate = coordinates[end + 1];

    const areCoordinatesOverlapping = areIntervalsOverlapping(
      {
        start: prevCoordinate.event.startTime,
        end: prevCoordinate.event.endTime,
      },
      {
        start: nextCoordinate.event.startTime,
        end: nextCoordinate.event.endTime,
      }
    );

    if (areCoordinatesOverlapping) {
      end++;
      continue;
    }

    end = end + 1;
    overlappingCoordinatesGroup.push(coordinates.slice(start, end));
    start = end;
  }

  // Unroll overlapping groups and combine them into final list of coordinates
  const finalCoordinates: EventCoordinate<TEvent>[] = [];

  overlappingCoordinatesGroup.forEach((group) => {
    const coordinateWidthRatio = 1 / group.length;

    for (let i = 0; i < group.length; i++) {
      const coordinate = group[i];

      coordinate.width = maxWidth * coordinateWidthRatio;
      coordinate.left = coordinate.width * i;

      finalCoordinates.push(coordinate);
    }
  });

  // #endregion

  return finalCoordinates;
}

const styles = stylex.create({
  root: {
    height: "100%",
    width: "100%",
    display: "flex",
  },
  leftSide: {},
  rightSide: {
    width: "100%",
  },
  topLeftCell: {
    height: HEADER_HEIGHT,
    width: LEFT_COLUMN_WIDTH,
    boxSizing: "border-box",
    borderRight: `1px solid ${cssVariables.borderColorDefault}`,
  },
  header: {
    display: "flex",
    flexWrap: "nowrap",
    overflowX: "auto",
  },
  headerCell: {
    height: HEADER_HEIGHT,
    minWidth: CELL_WIDTH,
    boxSizing: "border-box",
    borderRight: `1px solid ${cssVariables.borderColorDefault}`,
    borderBottom: `1px solid ${cssVariables.borderColorDefault}`,
  },
  hoursColumn: {
    overflowY: "auto",
    position: "relative",
    width: LEFT_COLUMN_WIDTH,
    boxSizing: "border-box",
    borderRight: `1px solid ${cssVariables.borderColorDefault}`,
  },
  hoursCell: {
    height: CELL_HEIGHT,
    paddingLeft: 16,
    paddingRight: 8,
  },
  hoursEmptyCell: {
    height: CELL_HEIGHT - 8,
  },
  hoursLastCell: {
    height: CELL_HEIGHT + 8,
  },
  grid: {
    display: "flex",
    flexWrap: "nowrap",
    overflow: "auto",
    position: "relative",
  },
  gridColumn: {
    minWidth: CELL_WIDTH,
    position: "relative",
    boxSizing: "border-box",
    borderRight: `1px solid ${cssVariables.borderColorDefault}`,
  },
  event: {
    background: "none",
    position: "absolute",
    overflow: "hidden",
    boxSizing: "border-box",
    padding: 0,
    margin: 0,
    boxShadow: "none",
    border: "none",
    borderRadius: cssVariables.radius,
  },
  hourLinesWrapper: {
    position: "absolute",
  },
  hourLine: {
    height: CELL_HEIGHT,
  },
  gridColumnBackground: {
    backgroundColor: "transparent",
    position: "absolute",
    width: "100%",
    height: "100%",
  },
});
